import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface CariIysIzin {
    iysIzinID: number;
    cariID: number;
    iletisimTipi: string;
    iletisimVerisi: string;
    izinDurumu: string;
    onayYontemi: string;
    evrakYolu: string | null;
    islemTarihi: string;
}

interface IysIzinModalProps {
    cariID: number;
    iletisimTipi: 'TELEFON1' | 'TELEFON2' | 'EPOSTA';
    iletisimVerisi: string;
    onClose: () => void;
    onSuccess: () => void;
    leadID?: number;
}

const yontemLabels: Record<string, string> = {
    SMS_KOD: 'SMS ile Kod',
    WEB_FORM: 'Web Formu / Beyan',
    YAZILI_BELGE: 'Yazılı Belge İmzalat',
    EPOSTA_KOD: 'E-posta ile Kod',
};

const IysIzinModal: React.FC<IysIzinModalProps> = ({
    cariID, iletisimTipi, iletisimVerisi, onClose, onSuccess, leadID
}) => {
    const [mevcutIzinler, setMevcutIzinler] = useState<CariIysIzin[]>([]);
    const [step, setStep] = useState<'yontem' | 'webform' | 'belge' | 'sms' | 'eposta'>('yontem');
    const [loading, setLoading] = useState(false);

    // Web form state
    const [islemTarihi, setIslemTarihi] = useState(() => new Date().toISOString().slice(0, 10));

    // Belge state
    const [belgeFile, setBelgeFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // SMS / E-posta state
    const [kodID, setKodID] = useState<number | null>(null);
    const [girilenKod, setGirilenKod] = useState('');
    const [kodGonderildi, setKodGonderildi] = useState(false);

    const isTelefon = iletisimTipi === 'TELEFON1' || iletisimTipi === 'TELEFON2';

    useEffect(() => {
        const url = leadID
            ? `/api/CariIysIzinleri/lead/${leadID}`
            : (cariID ? `/api/CariIysIzinleri/cari/${cariID}` : null);
        if (url) {
            axios.get(url)
                .then(res => {
                    setMevcutIzinler(res.data.filter((i: CariIysIzin) =>
                        i.iletisimVerisi === iletisimVerisi
                    ));
                })
                .catch(() => {});
        }
    }, [cariID, leadID, iletisimVerisi]);

    const reset = () => {
        setStep('yontem');
        setKodGonderildi(false);
        setKodID(null);
        setGirilenKod('');
        setBelgeFile(null);
    };

    // -------- Web Form handlerId --------
    const handleWebForm = async () => {
        setLoading(true);
        try {
            await axios.post('/api/CariIysIzinleri/web-form', {
                cariID,
                iletisimTipi,
                iletisimVerisi,
                islemTarihi: new Date(islemTarihi).toISOString(),
                leadID: leadID ?? null
            });
            toast.success('IYS izni kaydedildi.');
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // -------- Belge --------
    const handleBelgeYukle = async () => {
        if (!belgeFile) { toast.warn('Lütfen evrak dosyası seçin.'); return; }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('evrak', belgeFile);
            fd.append('cariID', String(cariID));
            fd.append('iletisimTipi', iletisimTipi);
            fd.append('iletisimVerisi', iletisimVerisi);
            if (leadID) fd.append('leadID', String(leadID));
            await axios.post('/api/CariIysIzinleri/belge-yukle', fd);
            toast.success('Evrak yüklendi, IYS izni kaydedildi.');
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Yükleme hatası.');
        } finally {
            setLoading(false);
        }
    };

    // -------- SMS Kod --------
    const handleSmsGonder = async () => {
        setLoading(true);
        try {
            const res = await axios.post('/api/CariIysIzinleri/sms-kod-gonder', { cariID, iletisimVerisi, leadID: leadID ?? null });
            setKodID(res.data.kodID);
            setKodGonderildi(true);
            toast.info(`Kod ${iletisimVerisi} numarasına gönderildi.`);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Kod gönderilemedi.');
        } finally {
            setLoading(false);
        }
    };

    const handleSmsDogrula = async () => {
        if (!girilenKod || !kodID) return;
        setLoading(true);
        try {
            await axios.post('/api/CariIysIzinleri/sms-kod-dogrula', {
                kodID, kod: girilenKod, iletisimTipi, cariID, leadID: leadID ?? null
            });
            toast.success('Kod doğrulandı, IYS izni kaydedildi.');
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Doğrulama hatası.');
        } finally {
            setLoading(false);
        }
    };

    // -------- E-posta Kod --------
    const handleEpostaGonder = async () => {
        setLoading(true);
        try {
            const res = await axios.post('/api/CariIysIzinleri/eposta-kod-gonder', { cariID, iletisimVerisi, leadID: leadID ?? null });
            setKodID(res.data.kodID);
            setKodGonderildi(true);
            toast.info(`Kod ${iletisimVerisi} adresine gönderildi.`);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Kod gönderilemedi.');
        } finally {
            setLoading(false);
        }
    };

    const handleEpostaOnayla = async () => {
        if (!girilenKod || !kodID) return;
        setLoading(true);
        try {
            await axios.post('/api/CariIysIzinleri/eposta-kod-onayla', {
                kodID, kod: girilenKod, iletisimTipi, cariID, leadID: leadID ?? null
            });
            toast.success('Kod onaylandı, IYS izni kaydedildi.');
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Onaylama hatası.');
        } finally {
            setLoading(false);
        }
    };

    // -------- Render --------
    const tipiLabel = isTelefon ? `Telefon: ${iletisimVerisi}` : `E-posta: ${iletisimVerisi}`;

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10500, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={onClose}>
            <div style={{
                backgroundColor: '#fff', borderRadius: 8, width: 480,
                maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
                padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.18)'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h5 style={{ margin: 0, color: '#1a2440' }}>IYS İzni Al</h5>
                        <small style={{ color: '#666' }}>{tipiLabel}</small>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: 20,
                        cursor: 'pointer', color: '#666', lineHeight: 1
                    }}>×</button>
                </div>

                {/* Mevcut izinler */}
                {mevcutIzinler.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#444' }}>Mevcut Kayıtlar</div>
                        {mevcutIzinler.map(iz => (
                            <div key={iz.iysIzinID} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                background: iz.izinDurumu === 'ONAY' ? '#f0fdf4' : '#fff5f5',
                                border: `1px solid ${iz.izinDurumu === 'ONAY' ? '#86efac' : '#fca5a5'}`,
                                borderRadius: 6, padding: '6px 10px', marginBottom: 4, fontSize: 13
                            }}>
                                <span>
                                    <span style={{
                                        background: iz.izinDurumu === 'ONAY' ? '#22c55e' : '#ef4444',
                                        color: '#fff', borderRadius: 4, padding: '2px 6px', marginRight: 8, fontSize: 11
                                    }}>{iz.izinDurumu}</span>
                                    {yontemLabels[iz.onayYontemi] || iz.onayYontemi}
                                </span>
                                <span style={{ color: '#888' }}>
                                    {new Date(iz.islemTarihi).toLocaleDateString('tr-TR')}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <hr style={{ margin: '0 0 16px' }} />

                {/* STEP: Yöntem Seçimi */}
                {step === 'yontem' && (
                    <div>
                        <div style={{ fontSize: 14, marginBottom: 12, color: '#444' }}>Onay yöntemini seçin:</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {isTelefon && (
                                <>
                                    <button onClick={() => setStep('sms')} className="iys-yontem-btn">
                                        <i className="fas fa-sms" style={{ marginRight: 8 }}></i>SMS ile Doğrulama Kodu
                                    </button>
                                    <button onClick={() => setStep('webform')} className="iys-yontem-btn">
                                        <i className="fas fa-file-alt" style={{ marginRight: 8 }}></i>Web Formu / Beyan
                                    </button>
                                    <button onClick={() => setStep('belge')} className="iys-yontem-btn">
                                        <i className="fas fa-file-signature" style={{ marginRight: 8 }}></i>Yazılı Belge İmzalat
                                    </button>
                                </>
                            )}
                            {!isTelefon && (
                                <>
                                    <button onClick={() => setStep('webform')} className="iys-yontem-btn">
                                        <i className="fas fa-file-alt" style={{ marginRight: 8 }}></i>Web Formu / Beyan
                                    </button>
                                    <button onClick={() => setStep('eposta')} className="iys-yontem-btn">
                                        <i className="fas fa-envelope" style={{ marginRight: 8 }}></i>E-posta ile Doğrulama Kodu
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* STEP: Web Form */}
                {step === 'webform' && (
                    <div>
                        <button onClick={reset} style={{ background: 'none', border: 'none', color: '#e67d21', cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                            ← Geri
                        </button>
                        <div style={{ fontSize: 14, marginBottom: 8 }}>Müşteri web formu doldururken izin beyan etmiştir.</div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>İşlem Tarihi</label>
                            <input type="date" value={islemTarihi}
                                onChange={e => setIslemTarihi(e.target.value)}
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 5 }} />
                        </div>
                        <button onClick={handleWebForm} disabled={loading} style={{
                            background: '#e67d21', color: '#fff', border: 'none', borderRadius: 5,
                            padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600
                        }}>
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                )}

                {/* STEP: Yazılı Belge */}
                {step === 'belge' && (
                    <div>
                        <button onClick={reset} style={{ background: 'none', border: 'none', color: '#e67d21', cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                            ← Geri
                        </button>
                        <div style={{ fontSize: 14, marginBottom: 10 }}>İmzalı evrakı sisteme yükleyin.</div>
                        <div
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={e => {
                                e.preventDefault(); setDragOver(false);
                                const f = e.dataTransfer.files[0];
                                if (f) setBelgeFile(f);
                            }}
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: `2px dashed ${dragOver ? '#e67d21' : '#ccc'}`,
                                borderRadius: 8, padding: 24, textAlign: 'center',
                                cursor: 'pointer', marginBottom: 12,
                                background: dragOver ? '#fff8f3' : '#fafafa'
                            }}>
                            {belgeFile
                                ? <span style={{ color: '#333' }}>📄 {belgeFile.name}</span>
                                : <span style={{ color: '#888' }}>Dosyayı sürükleyin veya tıklayın<br /><small>PDF, JPG, PNG — maks 5MB</small></span>
                            }
                        </div>
                        <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={e => { if (e.target.files?.[0]) setBelgeFile(e.target.files[0]); }} />
                        <button onClick={handleBelgeYukle} disabled={loading || !belgeFile} style={{
                            background: '#e67d21', color: '#fff', border: 'none', borderRadius: 5,
                            padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600
                        }}>
                            {loading ? 'Yükleniyor...' : 'Evrakı Yükle ve Kaydet'}
                        </button>
                    </div>
                )}

                {/* STEP: SMS Kod */}
                {step === 'sms' && (
                    <div>
                        <button onClick={reset} style={{ background: 'none', border: 'none', color: '#e67d21', cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                            ← Geri
                        </button>
                        {!kodGonderildi ? (
                            <div>
                                <div style={{ fontSize: 14, marginBottom: 12 }}>
                                    <b>{iletisimVerisi}</b> numarasına 6 haneli SMS kodu gönderilecek.
                                </div>
                                <button onClick={handleSmsGonder} disabled={loading} style={{
                                    background: '#e67d21', color: '#fff', border: 'none', borderRadius: 5,
                                    padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600
                                }}>
                                    {loading ? 'Gönderiliyor...' : 'SMS Kodu Gönder'}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div style={{ fontSize: 14, marginBottom: 8 }}>
                                    Müşteriden aldığınız kodu girin:
                                </div>
                                <input
                                    type="text" maxLength={6} value={girilenKod}
                                    onChange={e => setGirilenKod(e.target.value.replace(/\D/g, ''))}
                                    placeholder="6 haneli kod"
                                    style={{
                                        width: '100%', padding: '8px 12px', border: '1px solid #ccc',
                                        borderRadius: 5, marginBottom: 10, fontSize: 18,
                                        letterSpacing: 6, textAlign: 'center'
                                    }} />
                                <button onClick={handleSmsDogrula} disabled={loading || girilenKod.length !== 6} style={{
                                    background: '#22c55e', color: '#fff', border: 'none', borderRadius: 5,
                                    padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600, marginBottom: 8
                                }}>
                                    {loading ? 'Doğrulanıyor...' : 'Kodu Doğrula'}
                                </button>
                                <button onClick={handleSmsGonder} disabled={loading} style={{
                                    background: 'none', border: '1px solid #ccc', borderRadius: 5,
                                    padding: '6px 14px', cursor: 'pointer', width: '100%', color: '#555', fontSize: 13
                                }}>
                                    Tekrar Gönder
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP: E-posta Kod */}
                {step === 'eposta' && (
                    <div>
                        <button onClick={reset} style={{ background: 'none', border: 'none', color: '#e67d21', cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                            ← Geri
                        </button>
                        {!kodGonderildi ? (
                            <div>
                                <div style={{ fontSize: 14, marginBottom: 12 }}>
                                    <b>{iletisimVerisi}</b> adresine 6 haneli onay kodu gönderilecek.
                                </div>
                                <button onClick={handleEpostaGonder} disabled={loading} style={{
                                    background: '#e67d21', color: '#fff', border: 'none', borderRadius: 5,
                                    padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600
                                }}>
                                    {loading ? 'Gönderiliyor...' : 'E-posta Kodu Gönder'}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div style={{ fontSize: 14, marginBottom: 8 }}>
                                    Müşteriden aldığınız kodu girin:
                                </div>
                                <input
                                    type="text" maxLength={6} value={girilenKod}
                                    onChange={e => setGirilenKod(e.target.value.replace(/\D/g, ''))}
                                    placeholder="6 haneli kod"
                                    style={{
                                        width: '100%', padding: '8px 12px', border: '1px solid #ccc',
                                        borderRadius: 5, marginBottom: 10, fontSize: 18,
                                        letterSpacing: 6, textAlign: 'center'
                                    }} />
                                <button onClick={handleEpostaOnayla} disabled={loading || girilenKod.length !== 6} style={{
                                    background: '#22c55e', color: '#fff', border: 'none', borderRadius: 5,
                                    padding: '8px 20px', cursor: 'pointer', width: '100%', fontWeight: 600, marginBottom: 8
                                }}>
                                    {loading ? 'Onaylanıyor...' : 'Kodu Onayla'}
                                </button>
                                <button onClick={handleEpostaGonder} disabled={loading} style={{
                                    background: 'none', border: '1px solid #ccc', borderRadius: 5,
                                    padding: '6px 14px', cursor: 'pointer', width: '100%', color: '#555', fontSize: 13
                                }}>
                                    Tekrar Gönder
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                .iys-yontem-btn {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    padding: 10px 16px;
                    text-align: left;
                    cursor: pointer;
                    font-size: 14px;
                    transition: background 0.15s, border-color 0.15s;
                }
                .iys-yontem-btn:hover {
                    background: #fff3e0;
                    border-color: #e67d21;
                    color: #e67d21;
                }
            `}</style>
        </div>
    );
};

export default IysIzinModal;
